import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Pencil, Bug, PlusCircle, FolderPlus, Trash2 } from 'lucide-react';
import * as sprintBoardService from '../lib/sprintBoardService';
import * as sprintBoardAdminAuth from '../lib/sprintBoardAdminAuth';
import { PAGE_CHROME_GRAY_50 } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

const COLUMNS = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'planned', label: 'Planned' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'ready-to-test', label: 'Ready to Test' },
  { value: 'done', label: 'Done' },
] as const;

const STATUS_OPTIONS = COLUMNS.map((c) => ({ value: c.value, label: c.label }));

interface Epic {
  id: string;
  title: string;
  status: string;
}

const NO_EPIC_ID = '__no_epic__';

interface Story {
  id: string;
  epicId: string | null;
  title: string;
  assignee: string;
  status: string;
  issueType: 'story' | 'bug';
  userStory?: string;
  acceptanceCriteria?: string[];
  testCases?: string[];
}

/** Draft-only fields for edit form (textarea values before split) */
interface StoryDraft extends Story {
  acceptanceCriteriaText?: string;
  testCasesText?: string;
}

interface SprintData {
  projectName: string;
  lastUpdated: string;
  epics: Epic[];
  stories: Story[];
}

const assigneeClass = (assignee: string): string => {
  if (!assignee) return 'bg-gray-100 text-gray-600';
  if (assignee.includes(',') || (assignee.includes('Muge') && assignee.includes('Akin')))
    return 'bg-gray-200 text-gray-700';
  if (assignee.trim() === 'Akin') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  if (assignee.trim() === 'Muge') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
  return 'bg-gray-100 text-gray-600';
};

const CreateEpicModal: React.FC<{
  onClose: () => void;
  onCreated: () => void;
  existingEpics: Epic[];
}> = ({ onClose, onCreated, existingEpics }) => {
  const [title, setTitle] = useState('');
  const [storyRows, setStoryRows] = useState<{ title: string; type: 'story' | 'bug'; assignee: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const addRow = () => setStoryRows((r) => [...r, { title: '', type: 'story', assignee: '' }]);
  const removeRow = (i: number) => setStoryRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, upd: Partial<{ title: string; type: 'story' | 'bug'; assignee: string }>) => {
    setStoryRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...upd } : row)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const t = title.trim();
    if (!t) {
      setError('Epic title is required.');
      return;
    }
    for (let i = 0; i < storyRows.length; i++) {
      if (!storyRows[i].title.trim()) {
        setError(`Story ${i + 1} title is required.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const epic = await sprintBoardService.createEpic(existingEpics, t);
      if (!epic) {
        setError('Failed to create epic.');
        return;
      }
      const storiesAfterEpic = await sprintBoardService.fetchSprintData();
      let currentStories = storiesAfterEpic?.stories ?? [];
      for (const row of storyRows) {
        const created = await sprintBoardService.createStory(
          { epicId: epic.id, title: row.title.trim(), assignee: row.assignee.trim() || undefined, issueType: row.type },
          currentStories
        );
        if (created) currentStories = [...currentStories, created];
      }
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New epic</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Epic title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
              placeholder="e.g. Notifications"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Add stories to this epic (optional)</label>
              <button type="button" onClick={addRow} className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                Add story
              </button>
            </div>
            {storyRows.map((row, i) => (
              <div key={i} className="flex gap-2 items-start mb-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                <input
                  type="text"
                  value={row.title}
                  onChange={(e) => updateRow(i, { title: e.target.value })}
                  placeholder="Story title"
                  className="flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1.5 text-sm"
                />
                <select
                  value={row.type}
                  onChange={(e) => updateRow(i, { type: e.target.value as 'story' | 'bug' })}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1.5 text-sm"
                >
                  <option value="story">Story</option>
                  <option value="bug">Bug</option>
                </select>
                <input
                  type="text"
                  value={row.assignee}
                  onChange={(e) => updateRow(i, { assignee: e.target.value })}
                  placeholder="Assignee"
                  className="w-24 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1.5 text-sm"
                />
                <button type="button" onClick={() => removeRow(i)} className="text-gray-500 hover:text-red-600 p-1" aria-label="Remove">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg hover:bg-[var(--action-primary-pressed)] text-sm font-medium disabled:opacity-50">
              {submitting ? 'Creating…' : 'Create epic'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm font-medium">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateStoryModal: React.FC<{
  onClose: () => void;
  onCreated: () => void;
  epics: Epic[];
  existingStories: Story[];
}> = ({ onClose, onCreated, epics, existingStories }) => {
  const [title, setTitle] = useState('');
  const [epicId, setEpicId] = useState<string | null>(null);
  const [issueType, setIssueType] = useState<'story' | 'bug'>('story');
  const [assignee, setAssignee] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const t = title.trim();
    if (!t) {
      setError('Title is required.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await sprintBoardService.createStory(
        { epicId, title: t, assignee: assignee.trim() || undefined, issueType },
        existingStories
      );
      if (created) onCreated();
      else setError('Failed to create story.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New story</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Type</label>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value as 'story' | 'bug')}
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
            >
              <option value="story">Story</option>
              <option value="bug">Bug</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Epic (optional)</label>
            <select
              value={epicId ?? ''}
              onChange={(e) => setEpicId(e.target.value === '' ? null : e.target.value)}
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
            >
              <option value="">No epic</option>
              {epics.map((epic) => (
                <option key={epic.id} value={epic.id}>{epic.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
              placeholder="e.g. View and mark notifications as read"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Assignee (optional)</label>
            <input
              type="text"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              placeholder="e.g. Akin, Muge"
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg hover:bg-[var(--action-primary-pressed)] text-sm font-medium disabled:opacity-50">
              {submitting ? 'Creating…' : 'Create'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm font-medium">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SprintBoardLoginScreen: React.FC<{
  onSuccess: () => void;
  onError: (message: string) => void;
  error: string | null;
}> = ({ onSuccess, onError, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onError('');
    setSubmitting(true);
    try {
      const result = await sprintBoardAdminAuth.verifySprintBoardAdmin(email, password);
      if (result.ok) onSuccess();
      else onError(result.message ?? 'Invalid email or password');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-lg">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Sprint Board</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Sign in with your admin account.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="sb-admin-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              id="sb-admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
              required
            />
          </div>
          <div>
            <label htmlFor="sb-admin-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <input
              id="sb-admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
              required
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg hover:bg-[var(--action-primary-pressed)] disabled:opacity-50 text-sm font-medium"
            >
              {submitting ? 'Signing in…' : 'Log in'}
            </button>
            <Link to="/" className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
              Back to app
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

/** Board content: only mounted when admin is authenticated. Keeps hook count stable. */
const SprintBoardContent: React.FC<{ onSignOut: () => void }> = ({ onSignOut }) => {
  const [data, setData] = useState<SprintData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigneeFilter, setAssigneeFilter] = useState<'all' | 'Akin' | 'Muge'>('all');
  const [epicFilter, setEpicFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [editingDraft, setEditingDraft] = useState<StoryDraft | null>(null);
  const [draggingStoryId, setDraggingStoryId] = useState<string | null>(null);
  const [createEpicOpen, setCreateEpicOpen] = useState(false);
  const [createStoryOpen, setCreateStoryOpen] = useState(false);

  const usingSupabase = useRef(false);

  const updateStory = (storyId: string, updates: Partial<Story>) => {
    setData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        stories: prev.stories.map((s) =>
          s.id === storyId ? { ...s, ...updates } : s
        ),
      };
    });
    if (usingSupabase.current) {
      sprintBoardService.updateStory(storyId, {
        title: updates.title,
        assignee: updates.assignee,
        status: updates.status,
        epicId: updates.epicId,
        issueType: updates.issueType,
        userStory: updates.userStory,
        acceptanceCriteria: updates.acceptanceCriteria,
        testCases: updates.testCases,
      }).catch(() => { /* optimistic update already applied */ });
    }
  };

  useEffect(() => {
    let unsubRealtime: (() => void) | null = null;

    const load = async () => {
      const supabaseData = await sprintBoardService.fetchSprintData();
      if (!supabaseData) {
        throw new Error('Failed to load sprint board from Supabase. Run database_sprint_board.sql and database_sprint_board_realtime.sql.');
      }
      setData(supabaseData);
      usingSupabase.current = true;
      unsubRealtime = sprintBoardService.subscribeSprintBoard(async () => {
        const next = await sprintBoardService.fetchSprintData();
        if (next) setData(next);
      });
    };

    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
    return () => {
      if (unsubRealtime) unsubRealtime();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <p className="text-gray-600 dark:text-gray-400">Loading sprint board…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-2">{error || 'No data'}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Run <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">database_sprint_board.sql</code> and{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">database_sprint_board_realtime.sql</code> in Supabase, then seed epics and stories.
          </p>
        </div>
      </div>
    );
  }

  const epicsById: Record<string, Epic> = {};
  data.epics.forEach((e) => (epicsById[e.id] = e));

  const matchesAssignee = (story: Story): boolean => {
    if (assigneeFilter === 'all') return true;
    const a = (story.assignee || '').toLowerCase();
    if (assigneeFilter === 'Akin') return a.includes('akin');
    if (assigneeFilter === 'Muge') return a.includes('muge');
    return true;
  };

  const matchesEpic = (story: Story): boolean => {
    if (epicFilter === 'all') return true;
    if (epicFilter === NO_EPIC_ID) return story.epicId == null;
    return story.epicId === epicFilter;
  };

  const matchesSearch = (story: Story): boolean => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      story.id.toLowerCase().includes(q) ||
      (story.title ?? '').toLowerCase().includes(q)
    );
  };

  const stories = data.stories
    .filter(matchesAssignee)
    .filter(matchesEpic)
    .filter(matchesSearch);

  const backlog = stories.filter((s) => s.status === 'backlog');
  const planned = stories.filter((s) => s.status === 'planned' || s.status === 'sprint');
  const inProgress = stories.filter((s) => s.status === 'in-progress');
  const readyToTest = stories.filter((s) => s.status === 'ready-to-test');
  const done = stories.filter((s) => s.status === 'done');

  /** Group stories by epic; stories with no epic go under "No epic". */
  const groupByEpic = (list: Story[]): { epic: Epic; stories: Story[] }[] => {
    const byEpic = new Map<string, Story[]>();
    for (const s of list) {
      const key = s.epicId ?? NO_EPIC_ID;
      const arr = byEpic.get(key) ?? [];
      arr.push(s);
      byEpic.set(key, arr);
    }
    const noEpicVirtual: Epic = { id: NO_EPIC_ID, title: 'No epic', status: 'backlog' };
    const out: { epic: Epic; stories: Story[] }[] = data.epics
      .filter((e) => byEpic.has(e.id))
      .map((epic) => ({ epic, stories: byEpic.get(epic.id)! }));
    if (byEpic.has(NO_EPIC_ID)) {
      out.push({ epic: noEpicVirtual, stories: byEpic.get(NO_EPIC_ID)! });
    }
    return out;
  };

  const handleStatusChange = (story: Story, newStatus: string) => {
    updateStory(story.id, { status: newStatus });
    if (selectedStory?.id === story.id) {
      setSelectedStory({ ...selectedStory, status: newStatus });
    }
  };

  const handleDragStart = (e: React.DragEvent, story: Story) => {
    e.dataTransfer.setData('text/plain', story.id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingStoryId(story.id);
  };

  const handleDragEnd = () => setDraggingStoryId(null);

  const handleColumnDrop = (e: React.DragEvent, columnStatus: string) => {
    e.preventDefault();
    setDraggingStoryId(null);
    const storyId = e.dataTransfer.getData('text/plain');
    if (!storyId) return;
    const story = data?.stories.find((s) => s.id === storyId);
    if (story && story.status !== columnStatus) handleStatusChange(story, columnStatus);
  };

  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDeleteStory = async (story: Story, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${story.title}"?`)) return;
    const ok = await sprintBoardService.deleteStory(story.id);
    if (ok && selectedStory?.id === story.id) setSelectedStory(null);
  };

  const renderCard = (story: Story) => {
    const epic = story.epicId ? epicsById[story.epicId] : null;
    const epicTitle = epic ? epic.title : story.epicId ? `Epic ${story.epicId}` : 'No epic';
    const isBug = (story.issueType ?? 'story') === 'bug';
    const isDragging = draggingStoryId === story.id;
    return (
      <div
        key={story.id}
        draggable
        onDragStart={(e) => handleDragStart(e, story)}
        onDragEnd={handleDragEnd}
        onClick={() => !isDragging && setSelectedStory(story)}
        className={`bg-white dark:bg-gray-800 border rounded-lg p-3 mb-2 text-sm cursor-grab active:cursor-grabbing flex flex-col gap-2 transition-colors ${
          isDragging
            ? 'opacity-50 border-primary-500'
            : 'border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-400'
        }`}
      >
        <div className="flex items-start gap-2">
          <div className="font-medium text-gray-900 dark:text-gray-100 flex-1 min-w-0">
            <span className="text-gray-500 dark:text-gray-400 mr-1">{story.id}</span>
            {story.title}
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {isBug && (
              <span
                className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                title="Bug"
              >
                <Bug className="w-3.5 h-3.5" />
                Bug
              </span>
            )}
            <button
              type="button"
              onClick={(e) => handleDeleteStory(story, e)}
              className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400"
              title="Delete"
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className={`text-xs px-2 py-0.5 rounded ${assigneeClass(story.assignee)}`}>
            {story.assignee || '–'}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">{epicTitle}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 pb-24">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-primary-600 dark:text-primary-400 hover:underline text-sm">
              ← Back
            </Link>
            <button
              type="button"
              onClick={onSignOut}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Sign out (admin)
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCreateEpicOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <FolderPlus className="w-4 h-4" /> New epic
            </button>
            <button
              type="button"
              onClick={() => setCreateStoryOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg text-sm font-medium hover:bg-[var(--action-primary-pressed)]"
            >
              <PlusCircle className="w-4 h-4" /> New story
            </button>
          </div>
        </div>
        <h1 className="text-2xl font-bold mb-1">Sprint Board</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex items-center gap-2">
          {data.projectName} · Last updated: {data.lastUpdated}
          {usingSupabase.current && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" title="Connected to Supabase – changes sync in real time">
              Live
            </span>
          )}
        </p>

        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="assignee"
                checked={assigneeFilter === 'all'}
                onChange={() => setAssigneeFilter('all')}
                className="text-primary-600"
              />
              All
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="assignee"
                checked={assigneeFilter === 'Akin'}
                onChange={() => setAssigneeFilter('Akin')}
                className="text-primary-600"
              />
              Akin (member dashboard)
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="assignee"
                checked={assigneeFilter === 'Muge'}
                onChange={() => setAssigneeFilter('Muge')}
                className="text-primary-600"
              />
              Muge (trainer dashboard)
            </label>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="search"
              placeholder="Search by ID or title…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Search stories"
            />
            <select
              value={epicFilter}
              onChange={(e) => setEpicFilter(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Filter by epic"
            >
              <option value="all">All epics</option>
              <option value={NO_EPIC_ID}>No epic</option>
              {data.epics.map((epic) => (
                <option key={epic.id} value={epic.id}>
                  {epic.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { status: 'backlog', label: 'Backlog', list: backlog, headerClass: 'text-gray-500 dark:text-gray-400' },
            { status: 'planned', label: 'Planned', list: planned, headerClass: 'text-slate-600 dark:text-slate-400' },
            { status: 'in-progress', label: 'In Progress', list: inProgress, headerClass: 'text-amber-600 dark:text-amber-400' },
            { status: 'ready-to-test', label: 'Ready to Test', list: readyToTest, headerClass: 'text-blue-600 dark:text-blue-400' },
            { status: 'done', label: 'Done', list: done, headerClass: 'text-green-600 dark:text-green-400' },
          ].map(({ status, label, list, headerClass }) => {
            const groups = groupByEpic(list);
            return (
              <div
                key={status}
                onDragOver={handleColumnDragOver}
                onDrop={(e) => handleColumnDrop(e, status)}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 min-h-[200px]"
              >
                <h2 className={`text-xs font-semibold uppercase tracking-wide mb-3 pb-2 border-b border-gray-200 dark:border-gray-700 ${headerClass}`}>
                  {label} ({list.length})
                </h2>
                <div className="space-y-4">
                  {groups.length === 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic py-2">
                      {status === 'backlog' && 'Stories not yet planned for a sprint.'}
                      {status === 'planned' && 'Stories committed to this sprint.'}
                      {status === 'in-progress' && 'Stories currently being worked on.'}
                      {status === 'ready-to-test' && 'Stories waiting for verification.'}
                      {status === 'done' && 'Stories completed and tested.'}
                    </p>
                  ) : (
                    groups.map(({ epic, stories: epicStories }) => (
                      <div key={epic.id}>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 truncate" title={epic.title}>
                          {epic.title} ({epicStories.length})
                        </p>
                        {epicStories.map(renderCard)}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Epic modal */}
      {createEpicOpen && data && (
        <CreateEpicModal
          onClose={() => setCreateEpicOpen(false)}
          onCreated={() => {
            setCreateEpicOpen(false);
            sprintBoardService.fetchSprintData().then((next) => next && setData(next));
          }}
          existingEpics={data.epics}
        />
      )}

      {/* Create Story modal */}
      {createStoryOpen && data && (
        <CreateStoryModal
          onClose={() => setCreateStoryOpen(false)}
          onCreated={() => {
            setCreateStoryOpen(false);
            sprintBoardService.fetchSprintData().then((next) => next && setData(next));
          }}
          epics={data.epics}
          existingStories={data.stories}
        />
      )}

      {/* Story detail modal */}
      {selectedStory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => {
            setSelectedStory(null);
            setEditingDraft(null);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 id="modal-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingDraft ? editingDraft.title : selectedStory.title}
                </h3>
                <div className="flex flex-wrap gap-2 mt-2 items-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{selectedStory.id}</span>
                  {(editingDraft ?? selectedStory).issueType === 'bug' && (
                    <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                      <Bug className="w-3.5 h-3.5" /> Bug
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded ${assigneeClass((editingDraft ?? selectedStory).assignee)}`}>
                    {(editingDraft ?? selectedStory).assignee || '–'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedStory.epicId ? (epicsById[selectedStory.epicId]?.title ?? `Epic ${selectedStory.epicId}`) : 'No epic'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                    {(editingDraft ?? selectedStory).status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!editingDraft ? (
                  <button
                    type="button"
                    onClick={() => setEditingDraft({ ...selectedStory })}
                    className="p-2 text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 rounded"
                    aria-label="Edit story"
                    title="Edit story"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStory(null);
                    setEditingDraft(null);
                  }}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto text-sm space-y-4">
              {editingDraft ? (
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const acText = editingDraft.acceptanceCriteriaText ?? (editingDraft.acceptanceCriteria ?? []).join('\n');
                    const tcText = editingDraft.testCasesText ?? (editingDraft.testCases ?? []).join('\n');
                    const ac = acText.split('\n').map((s) => s.trim()).filter(Boolean);
                    const tc = tcText.split('\n').map((s) => s.trim()).filter(Boolean);
                    updateStory(selectedStory.id, {
                      title: editingDraft.title,
                      assignee: editingDraft.assignee,
                      status: editingDraft.status,
                      epicId: editingDraft.epicId ?? null,
                      issueType: editingDraft.issueType ?? 'story',
                      userStory: editingDraft.userStory || undefined,
                      acceptanceCriteria: ac.length ? ac : undefined,
                      testCases: tc.length ? tc : undefined,
                    });
                    setSelectedStory({
                      ...selectedStory,
                      title: editingDraft.title,
                      assignee: editingDraft.assignee,
                      status: editingDraft.status,
                      epicId: editingDraft.epicId ?? null,
                      issueType: editingDraft.issueType ?? 'story',
                      userStory: editingDraft.userStory || undefined,
                      acceptanceCriteria: ac.length ? ac : undefined,
                      testCases: tc.length ? tc : undefined,
                    });
                    setEditingDraft(null);
                  }}
                >
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingDraft.title}
                      onChange={(e) => setEditingDraft({ ...editingDraft, title: e.target.value })}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Type</label>
                      <select
                        value={editingDraft.issueType ?? 'story'}
                        onChange={(e) => setEditingDraft({ ...editingDraft, issueType: e.target.value as 'story' | 'bug' })}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                      >
                        <option value="story">Story</option>
                        <option value="bug">Bug</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Epic</label>
                      <select
                        value={editingDraft.epicId ?? ''}
                        onChange={(e) => setEditingDraft({ ...editingDraft, epicId: e.target.value === '' ? null : e.target.value })}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                      >
                        <option value="">No epic</option>
                        {data.epics.map((epic) => (
                          <option key={epic.id} value={epic.id}>{epic.title}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Assignee</label>
                      <input
                        type="text"
                        value={editingDraft.assignee}
                        onChange={(e) => setEditingDraft({ ...editingDraft, assignee: e.target.value })}
                        placeholder="e.g. Akin, Muge"
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Status</label>
                      <select
                        value={editingDraft.status}
                        onChange={(e) => setEditingDraft({ ...editingDraft, status: e.target.value })}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">User story</label>
                    <textarea
                      value={editingDraft.userStory ?? ''}
                      onChange={(e) => setEditingDraft({ ...editingDraft, userStory: e.target.value })}
                      rows={3}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Acceptance criteria (one per line)</label>
                    <textarea
                      value={editingDraft.acceptanceCriteriaText ?? (editingDraft.acceptanceCriteria ?? []).join('\n')}
                      onChange={(e) => setEditingDraft({ ...editingDraft, acceptanceCriteriaText: e.target.value })}
                      rows={4}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Test cases (one per line)</label>
                    <textarea
                      value={editingDraft.testCasesText ?? (editingDraft.testCases ?? []).join('\n')}
                      onChange={(e) => setEditingDraft({ ...editingDraft, testCasesText: e.target.value })}
                      rows={4}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[var(--action-primary)] text-[var(--ink)] rounded-lg hover:bg-[var(--action-primary-pressed)] text-sm font-medium"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingDraft(null)}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {selectedStory.userStory && (
                    <section>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">User story</h4>
                      <p className="italic text-gray-600 dark:text-gray-300">{selectedStory.userStory}</p>
                    </section>
                  )}
                  {selectedStory.acceptanceCriteria && selectedStory.acceptanceCriteria.length > 0 && (
                    <section>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Acceptance criteria</h4>
                      <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
                        {selectedStory.acceptanceCriteria.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {selectedStory.testCases && selectedStory.testCases.length > 0 && (
                    <section>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Test cases</h4>
                      <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
                        {selectedStory.testCases.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
                    Full details: search for “Story {selectedStory.id}” in{' '}
                    <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">epics.md</code>.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const SprintBoard: React.FC = () => {
  const [adminAuthenticated, setAdminAuthenticated] = useState(sprintBoardAdminAuth.hasSprintBoardAdminSession());
  const [loginError, setLoginError] = useState<string | null>(null);

  usePageStatusBar({ background: PAGE_CHROME_GRAY_50 });

  if (!adminAuthenticated) {
    return (
      <SprintBoardLoginScreen
        onSuccess={() => {
          sprintBoardAdminAuth.setSprintBoardAdminSession();
          setAdminAuthenticated(true);
          setLoginError(null);
        }}
        onError={(message) => setLoginError(message)}
        error={loginError}
      />
    );
  }

  return (
    <SprintBoardContent
      onSignOut={() => {
        sprintBoardAdminAuth.signOutSprintBoardAdmin();
        setAdminAuthenticated(false);
      }}
    />
  );
};
